import { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { CallistoLogo } from "../components/CallistoLogo";
import { Loader2, ArrowLeft, MailCheck } from "lucide-react";

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const [devLink, setDevLink] = useState("");

  async function onSubmit(e) {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      setDone(true);
      if (data?.dev_reset_link) setDevLink(data.dev_reset_link);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen flex flex-col bg-callisto-app">
      <div className="px-6 py-5 bg-white border-b border-callisto">
        <CallistoLogo variant="light" size="md" />
      </div>
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md bg-white rounded-2xl border border-callisto shadow-sm p-8">
          {!done ? (
            <>
              <span className="text-[11px] font-bold uppercase tracking-[0.25em] text-callisto-gold">
                Forgot password
              </span>
              <h1 className="mt-2 text-2xl md:text-3xl font-bold text-callisto-navy">
                Reset your password
              </h1>
              <p className="mt-2 text-callisto-muted text-sm">
                Enter your work email and we'll send you a secure reset link.
              </p>

              <form onSubmit={onSubmit} className="mt-6 space-y-4" data-testid="forgot-password-form">
                <div className="space-y-2">
                  <Label htmlFor="fp-email" className="text-callisto-navy font-medium">
                    Work email
                  </Label>
                  <Input
                    id="fp-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@callistosfa.com"
                    required
                    autoComplete="email"
                    className="h-11 focus-visible:ring-callisto-gold focus-visible:ring-offset-0"
                    data-testid="forgot-password-email-input"
                  />
                </div>
                {error && (
                  <div className="rounded-md border border-red-200 bg-red-50 text-red-700 text-sm px-3 py-2">
                    {error}
                  </div>
                )}
                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full h-11 bg-callisto-navy hover:bg-callisto-navy/90 text-white font-semibold"
                  data-testid="forgot-password-submit-button"
                >
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send reset link"}
                </Button>
              </form>
            </>
          ) : (
            <div className="text-center" data-testid="forgot-password-success">
              <div className="h-14 w-14 rounded-full bg-callisto-gold/10 flex items-center justify-center mx-auto mb-4">
                <MailCheck className="h-6 w-6 text-callisto-gold" />
              </div>
              <h1 className="text-2xl font-bold text-callisto-navy">Check your email</h1>
              <p className="mt-2 text-callisto-muted text-sm">
                If an account exists for that email, we've just sent a password
                reset link.
              </p>
              {devLink && (
                <div className="mt-5 text-left rounded-md border border-callisto bg-callisto-app px-3 py-3">
                  <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-callisto-gold">
                    Dev reset link
                  </p>
                  <a
                    href={devLink}
                    className="mt-1 text-xs text-callisto-navy break-all underline"
                    data-testid="forgot-password-dev-link"
                  >
                    {devLink}
                  </a>
                </div>
              )}
            </div>
          )}

          <Link
            to="/login"
            className="mt-6 inline-flex items-center gap-1 text-sm text-callisto-navy hover:underline"
          >
            <ArrowLeft size={14} />
            Back to sign in
          </Link>
        </div>
      </div>
    </div>
  );
}
